import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const initial = { name: '', email: '', schoolTeam: '', role: '', preferredTime: '', website: '' };
const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_DEMO_CONVERSION_LABEL;

export default function BookDemo() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (!conversionId || !conversionLabel || document.querySelector('script[data-mick-demo-ads]')) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', conversionId);
    const script = document.createElement('script');
    script.async = true;
    script.dataset.mickDemoAds = 'true';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
    document.head.appendChild(script);
  }, []);

  async function submit(event) {
    event.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const response = await api.post('/demo/request', {
        ...form,
        preferredTime: new Date(form.preferredTime).toISOString(),
        timeZone
      });
      if (!response.data?.ok || !response.data?.requestId) throw new Error('No confirmation was returned.');
      setStatus('complete');
      if (conversionId && conversionLabel && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: `${conversionId}/${conversionLabel}`,
          transaction_id: response.data.requestId
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'We could not send your request. Please try again.');
      setStatus('idle');
    }
  }

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <div className="min-h-screen bg-brand-offwhite px-4 py-6 text-brand-black sm:py-10">
      <div className="mx-auto max-w-4xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/"><img src="/mick-oc-logo.png" alt="Mick OC home" className="w-40 rounded-lg bg-brand-black" /></Link>
          <Link to="/" className="text-sm font-semibold text-stone-700 underline">Back to Mick OC</Link>
        </header>
        <main className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <section>
            <p className="eyebrow">For football coaches</p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight sm:text-5xl">See your playbook in action.</h1>
            <p className="mt-5 text-lg leading-8 text-stone-700">Request a Mick OC demo. We’ll show how coaches enter their existing plays and get one run and one pass recommendation for a game situation.</p>
            <ul className="mt-6 list-disc space-y-3 pl-5 text-base leading-7 text-stone-700">
              <li>Recommendations from your team’s own playbook</li>
              <li>Voice or manual game-situation input</li>
              <li>Game-day calls available for later review</li>
            </ul>
            <p className="mt-6 text-sm leading-6 text-stone-600">Choose a preferred time below. We’ll confirm the appointment by email; your requested time is not reserved yet.</p>
          </section>
          <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
            {status === 'complete' ? (
              <div role="status">
                <h2 className="text-3xl font-semibold">Demo request received</h2>
                <p className="mt-4 text-base leading-7 text-stone-700">We emailed a receipt to {form.email}. We’ll follow up to confirm your appointment time.</p>
                <p className="mt-4 text-sm text-stone-600">Requested: {new Date(form.preferredTime).toLocaleString()} ({timeZone})</p>
                <Link to="/" className="mt-6 inline-flex rounded-lg bg-brand-orange px-5 py-3 font-semibold">Return to Mick OC</Link>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold">Request your demo</h2>
                <p className="mt-2 text-sm text-stone-600">Five quick details. No account or payment required.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  {[
                    ['name', 'Name', 'text', 'name'],
                    ['email', 'Email', 'email', 'email'],
                    ['schoolTeam', 'School or team', 'text', 'organization'],
                    ['role', 'Coaching role', 'text', 'organization-title'],
                  ].map(([name, label, type, autoComplete]) => (
                    <div key={name}>
                      <label htmlFor={name} className="mb-1 block text-sm font-semibold">{label}</label>
                      <input id={name} name={name} type={type} autoComplete={autoComplete} value={form[name]} onChange={update} required maxLength={name === 'email' ? 254 : 150} className="input-field w-full" />
                    </div>
                  ))}
                  <div>
                    <label htmlFor="preferredTime" className="mb-1 block text-sm font-semibold">Preferred date and time</label>
                    <input id="preferredTime" name="preferredTime" type="datetime-local" value={form.preferredTime} onChange={update} required className="input-field w-full" />
                    <p className="mt-1 text-xs text-stone-600">Shown in your local time zone: {timeZone}</p>
                  </div>
                  <div className="hidden" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" value={form.website} onChange={update} tabIndex="-1" autoComplete="off" /></div>
                  {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
                  <button type="submit" disabled={status === 'sending'} className="btn-primary w-full justify-center text-base">{status === 'sending' ? 'Sending request…' : 'Request demo'}</button>
                  <p className="text-xs leading-5 text-stone-600">We’ll use these details only to arrange your demo. See our <Link to="/privacy" className="underline">privacy policy</Link>.</p>
                </form>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
